import store from '@/store/index'

import {
  SEARCH_CATEGORIES,
  SEARCH_PURGE_SEARCH_RESULTS,
  SEARCH_SET_PAGINATION,
  SEARCH_SET_SEARCH_QUERY,
  SEARCH_SET_SEARCH_RESULTS,
  SEARCH_SET_CATEGORIES
} from '@/store/search/searchModule.types'
import {
  RECENT_HISTORY,
  RECENT_ADD_HISTORY,
  RECENT_REMOVE_HISTORY,
  RECENT_PURGE_HISTORY
} from '@/store/recent/recentHistoryModule.types'

export default {}

const uuidv5 = require('uuid/v5')
const uuidv4 = require('uuid/v4')

/**
 * Returns a random string of the specified length
 *
 * @param length int
 *
 * @return String
 */
export const randomString = (length = 6) => {
  let text = ''
  let possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'

  for (let i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length))
  }

  return text
}

/**
 * Generates and returns a UUID (v4 default) and optionally
 * can generate a unique id for the domain.
 *
 * @return string
 */
export const uuid = (version = 'v4') => {
  if (version === 'v5') {
    return uuidv5(process.env.DOMAIN, uuidv5.DNS)
  } else {
    return uuidv4()
  }
}

/**
 * Trims a string, if the string is empty, it will return null.
 *
 * @param value String|null
 * @returns String|null
 */
export const trimmify = (value) => {
  if (typeof value === 'string') {
    if (value.trim() === '') {
      return null
    } else {
      return value.trim()
    }
  } else {
    return value
  }
}

/**
 * If the given value is a string, converts the first
 * character to uppercase and returns the result, otherwise
 * returns the value as is.
 *
 * @param value mixed
 * @returns string|mixed
 */
export const firstCharToUpper = (value) => {
  if (typeof value === 'string') {
    return value.charAt(0).toUpperCase() + value.slice(1)
  } else {
    return value
  }
}

/**
 * Sorts an array of numeric values. The standard Array.sort()
 * method sorts an array alphabetically where-as this function
 * sorts numbers properly.
 *
 * @param values Array
 * @returns Array
 */
export const sortNumeric = (values) => {
  return values.sort((a, b) => (a - b))
}

/**
 * Returns the specified value, unless the value matches equalTo
 * in which case it will return the thenConvertTo value. Also
 * typecasts the value to toType.
 *
 * @param value mixed
 * @param equalTo mixed
 * @param thenConvertTo mixed
 * @param toType string|undefined
 *
 * @returns undefined|null|String|Number|Boolean
 */
export const as = (value, equalTo = null, thenConvertTo = null, toType = undefined) => {
  if (value === equalTo) {
    if (toType !== undefined) {
      switch (toType) {
        case 'string':
          return String(thenConvertTo)

        case 'number':
          return Number(thenConvertTo)

        case 'boolean':
          return Boolean(thenConvertTo)

        default:
          return value
      }
    } else {
      return thenConvertTo
    }
  } else {
    if (toType !== undefined) {
      switch (toType) {
        case 'string':
          return String(value)

        case 'number':
          return Number(value)

        case 'boolean':
          return Boolean(value)

        default:
          return value
      }
    } else {
      return value
    }
  }
}

/**
 * Converts and returns the value as a number, unless
 * it is null in which case null is returned.
 *
 * @param value mixed
 *
 * @returns null|Number
 */
export const asNumberOrNull = (value) => {
  return value === null ? value : Number(value)
}

/**
 * Converts and returns the value as a string that
 * represents a float with 2 decimal digits, unless
 * it is null or is not a valid float, in which case
 * null is returned.
 *
 * @param value mixed
 *
 * @returns null|String
 */
export const asCurrencyOrNull = (value) => {
  if (value === null) return null
  if (isNaN(parseFloat(value))) return null

  let result = String(value)
  if (result.includes('.')) {
    // Make sure we have only 2 digits after the .
    const tmp = result.split('.')
    return tmp[0] + '.' + tmp[1].substring(0, 2)
  } else {
    return result + '.00'
  }
}

/**
 * Converts and returns the value as a string, unless
 * it is empty or null in which case null is returned.
 *
 * @param value mixed
 *
 * @returns null|String
 */
export const asStringOrNull = (value) => {
  if (value === null || String(value) === '' || String(value) === '-1') {
    return null
  } else {
    return String(value)
  }
}

/**
 * Converts and returns the value as a boolean, unless
 * it is null in which case null is returned.
 *
 * @param value mixed
 *
 * @returns null|Boolean
 */
export const asBooleanOrNull = (value) => {
  return value === null ? value : Boolean(value)
}

/**
 * Extracts the value part of a Unit of Measurement type
 * string, i.e. "50 meters" will return 50 and
 * "32 celsius" will return 32. The return type can also
 * be specified.
 *
 * @param value string
 * @param toType undefined|string
 *
 * @return undefined|null|String|Number|Boolean
 */
export const extractUoMValue = (value, toType = 'number') => {
  const temp = value.replace(/  +/g, ' ').split(' ')

  switch (toType) {
    case 'string':
      return String(temp[0])

    case 'number':
      return Number(temp[0])

    case 'boolean':
      return Boolean(temp[0])

    default:
      return temp[0]
  }
}

/**
 * Extracts the unit part of a Unit of Measurement type
 * string, i.e. "50 meters" will return "meters" and
 * "32 celsius" will return "celsius".
 *
 * @param value string
 *
 * @return string
 */
export const extractUoMUnit = (value) => {
  const temp = value.replace(/  +/g, ' ').split(' ')
  if (temp.length < 2) {
    return ''
  } else {
    return temp[1]
  }
}

/**
 * Copies text to the system clipboard.
 *
 * @params text String
 */
export const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => {
    toastr.info(`Copied <strong>${text}</strong> to clipboard.`)
  })
}

/**
 * Mutates a property's value inside an object using
 * a dot notation path that defines the depth of the
 * object being mutated.
 *
 * Currently a maximum depth of 6 levels are allowed.
 *
 * @param obj Object
 * @param path String
 * @param value mixed The new value
 */
export const mutateProp = (obj, path, value) => {
  let props = path.split('.')

  if (props.length > 6) {
    throw new Error(`Property path can not be more than 6 levels deep, path received: ${path}`)
  }

  if (props.length === 1) {
    obj[props[0]] = value
  } else if (props.length === 2) {
    obj[props[0]][props[1]] = value
  } else if (props.length === 3) {
    obj[props[0]][props[1]][props[2]] = value
  } else if (props.length === 5) {
    obj[props[0]][props[1]][props[2]][props[3]][props[4]] = value
  } else if (props.length === 6) {
    obj[props[0]][props[1]][props[2]][props[3]][props[4]][props[5]] = value
  }
}

/**
 * Add an element to an array of a property on the given object. A dot notation path can be used to
 * specify the depth of the property in deep objects.
 *
 * Currently a maximum depth of 6 levels are allowed.
 *
 * @param obj Object
 * @param path String
 * @param value mixed The new value
 */
export const pushProp = (obj, path, value) => {
  let props = path.split('.')

  if (props.length > 6) {
    throw new Error(`Property path can not be more than 6 levels deep, path received: ${path}`)
  }

  if (props.length === 1) {
    obj[props[0]].push(value)
  } else if (props.length === 2) {
    obj[props[0]][props[1]].push(value)
  } else if (props.length === 3) {
    obj[props[0]][props[1]][props[2]].push(value)
  } else if (props.length === 5) {
    obj[props[0]][props[1]][props[2]][props[3]][props[4]].push(value)
  } else if (props.length === 6) {
    obj[props[0]][props[1]][props[2]][props[3]][props[4]][props[5]].push(value)
  }
}

/**
 * Removes and returns an element from an array of a property on the given object.
 * A dot notation path can be used to specify the depth of the property in deep objects.
 *
 * Currently a maximum depth of 6 levels are allowed.
 *
 * @param obj Object
 * @param path String
 * @param index Number The index of the element to remove
 *
 * @return mixed the element that was removed
 */
export const pullProp = (obj, path, index) => {
  let props = path.split('.')

  if (props.length > 6) {
    throw new Error(`Property path can not be more than 6 levels deep, path received: ${path}`)
  }

  let result

  if (props.length === 1) {
    result = obj[props[0]][index]
    obj[props[0]].splice(index, 1)
  } else if (props.length === 2) {
    result = obj[props[0]][props[1]][index]
    obj[props[0]][props[1]].splice(index, 1)
  } else if (props.length === 3) {
    result = obj[props[0]][props[1]][props[2]][index]
    obj[props[0]][props[1]][props[2]].splice(index, 1)
  } else if (props.length === 5) {
    result = obj[props[0]][props[1]][props[2]][props[3]][props[4]][index]
    obj[props[0]][props[1]][props[2]][props[3]][props[4]].splice(index, 1)
  } else if (props.length === 6) {
    result = obj[props[0]][props[1]][props[2]][props[3]][props[4]][props[5]][index]
    obj[props[0]][props[1]][props[2]][props[3]][props[4]][props[5]].splice(index, 1)
  }

  return result
}

/**
 * Returns true of the path ends with "/create".
 *
 * @param path String
 *
 * @returns Boolean
 */
export const isCreateMode = (path) => {
  return path.substring(path.length - 7, path.length) === '/create'
}

/**
 * Returns true of the path ends with "/edit".
 *
 * @param path String
 *
 * @returns Boolean
 */
export const isEditMode = (path) => {
  return path.substring(path.length - 5, path.length) === '/edit'
}

/**
 * Returns true of the path ends with "/view".
 *
 * @param path String
 *
 * @returns Boolean
 */
export const isViewMode = (path) => {
  return path.substring(path.length - 5, path.length) === '/view'
}

/**
 * Returns "create", "edit" or "view" based on whether the path
 * ends with "/create", "/edit" or "/view".
 *
 * @param path String
 *
 * @returns String
 */
export const formMode = (path) => {
  if (path.substring(path.length - 7, path.length) === '/create') {
    return 'create'
  } else if (path.substring(path.length - 5, path.length) === '/edit') {
    return 'edit'
  } else if (path.substring(path.length - 5, path.length) === '/view') {
    return 'view'
  } else {
    return ''
  }
}

/**
 * Returns a title starting with "Create", "Edit" or "View" based
 * on whether the path ends with "/create", "/edit" or "/view" and
 * postfixes it with the postfix provided.
 *
 * @param path    String
 * @param postfix String
 *
 * @returns String
 */
export const formModeTitle = (path, postfix = '') => {
  if (path.substring(path.length - 7, path.length) === '/create') {
    return 'Add ' + postfix
  } else if (path.substring(path.length - 5, path.length) === '/edit') {
    return 'Edit ' + postfix
  } else if (path.substring(path.length - 5, path.length) === '/view') {
    return 'View ' + postfix
  } else {
    return postfix
  }
}

/**
 * Calls the given module's "get" getter.
 *
 * @param module String
 * @param method String
 *
 * @returns mixed
 */
export const storeGet = (module, method = 'get') => {
  return store.getters[`${module}/${method}`]
}

/**
 * Calls the given module's "get" getter and returns with
 * the value of the specified property
 *
 * @param module   String
 * @param property String
 * @param method   String
 *
 * @returns mixed
 */
export const storeGetProp = (module, property, method = 'get') => {
  return store.getters[`${module}/${method}`][property]
}

/**
 * Return partners that is allowed to
 * be viewed by user. This can only be used
 * if the partners array has been set in the the module.
 *
 * @param module
 * @param method
 * @returns Array
 */
export const storeGetPartners = (module, method = 'getPartners') => {
  return store.getters[`${module}/${method}`]
}

/**
 * Return agencies that is allowed to
 * be viewed by user. This can only be used
 * if the agencies array has been set in the the module.
 *
 * @param module
 * @param method
 * @returns Array
 */
export const storeGetAgencies = (module, method = 'getAgencies') => {
  return store.getters[`${module}/${method}`]
}

/**
 * Calls the given module's "isModified" getter.
 *
 * @param module String
 * @param method String
 *
 * @returns Boolean
 */
export const storeIsModified = (module, method = 'isModified') => {
  return store.getters[`${module}/${method}`]
}

/**
 * Calls the given module's "isLoading" getter.
 *
 * @param module String
 * @param method String
 *
 * @returns Boolean
 */
export const storeIsLoading = (module, method = 'isLoading') => {
  return store.getters[`${module}/${method}`]
}

/**
 * Dispatches the given module's "query" action.
 *
 * @param module String
 * @param params Object
 * @param method String
 */
export const storeQuery = (module, params = {}, method = 'query') => {
  return store.dispatch(`${module}/${method}`, params)
}

/**
 * Calls the given module's "getForm" getter.
 *
 * @param module String
 * @param method String
 *
 * @returns mixed
 */
export const storeGetForm = (module, method = 'getForm') => {
  return store.getters[`${module}/${method}`]
}

/**
 * Calls the given module's "set" mutator.
 *
 * @param module  String
 * @param payload mixed
 * @param method  String
 */
export const storeSet = (module, payload, method = 'set') => {
  return store.commit(`${module}/${method}`, payload)
}

/**
 * Calls a commit method on the given module.
 *
 * @param module  String
 * @param payload mixed
 * @param method  String
 */
export const storeCommit = (module, payload, method) => {
  return store.commit(`${module}/${method}`, payload)
}

/**
 * Calls the given module's "setProp" mutator.
 *
 * @param module   String
 * @param property Object Example: { name: 'myProperty', value: 'Some Value' }
 * @param method   String
 */
export const storeSetProp = (module, property, method = 'setProp') => {
  return store.commit(`${module}/${method}`, property)
}

/**
 * Calls the given module's "pushProp" mutator and pushes
 * it to an array.
 *
 * @param module   String
 * @param property Object Example: { name: 'myProperty', value: [{ name: 'SomeName', surname: 'SomeSurname'}] }
 * @param method   String
 */
export const storePushProp = (module, property, method = 'pushProp') => {
  return store.commit(`${module}/${method}`, property)
}

/**
 * Calls the given module's "pullProp" mutator.
 *
 * @param module   String
 * @param name     String property name
 * @param index    Number index of element
 * @param method   String
 */
export const storePullProp = (module, name, index, method = 'pullProp') => {
  return store.commit(`${module}/${method}`, { name, index })
}

/**
 * Calls the given module's "spliceProp" mutator and removes
 * it from an array.
 *
 * @param module   String
 * @param property Object Example: { name: 'myProperty', index: 1, replacement: {} }
 * @param method   String
 */
export const storeSpliceProp = (module, property, method = 'spliceProp') => {
  return store.commit(`${module}/${method}`, property)
}

/**
 * Calls the given module's "setProps" mutator.
 *
 * @param module     String
 * @param properties Array Example: [{ name: 'myProperty', value: 'Some Value' }]
 * @param method     String
 */
export const storeSetProps = (module, properties, method = 'setProps') => {
  return store.commit(`${module}/${method}`, properties)
}

/**
 * Calls the given module's "setIsLoading" mutator.
 *
 * @param module    String
 * @param isLoading Boolean
 * @param method    String
 */
export const storeSetIsLoading = (module, isLoading, method = 'setIsLoading') => {
  return store.commit(`${module}/${method}`, isLoading)
}

/**
 * Calls the given module's "reset" mutator.
 *
 * @param module String
 * @param method String
 */
export const storeReset = (module, method = 'reset') => {
  return store.commit(`${module}/${method}`)
}

/**
 * Calls the given module's "resetForm" mutator.
 *
 * @param module String
 * @param method String
 */
export const storeResetForm = (module, method = 'resetForm') => {
  return store.commit(`${module}/${method}`)
}

/**
 * Calls the given module's "setIsModified" mutator.
 *
 * @param module String
 * @param isModified Boolean
 * @param method String
 */
export const storeSetIsModified = (module, isModified, method = 'setIsModified') => {
  return store.commit(`${module}/${method}`, isModified)
}

/**
 * Dispatches the given module's "fetch" action.
 *
 * @param module   String
 * @param resource String
 * @param method   String
 */
export const storeFetch = (module, resource, method = 'fetch') => {
  return store.dispatch(`${module}/${method}`, resource)
}

/**
 * Dispatches the given module's "fetch" action.
 *
 * @param module String
 * @param id     Number
 * @param method String
 */
export const storeFetchAgencies = (module, id, method = 'fetchAgencies') => {
  return store.dispatch(`${module}/${method}`, id)
}

/**
 * Dispatches the given module's "fetchFormCreate" action.
 *
 * @param module String
 * @param method String
 */
export const storeFetchFormCreate = (module, method = 'fetchFormCreate') => {
  return store.dispatch(`${module}/${method}`)
}

/**
 * Dispatches the given module's "fetchFormEdit" action.
 *
 * @param module   String
 * @param resource String
 * @param method   String
 */
export const storeFetchFormEdit = (module, resource, method = 'fetchFormEdit') => {
  return store.dispatch(`${module}/${method}`, resource)
}

/**
 * Dispatches the given module's "post" action.
 *
 * @param module String
 * @param data   Object
 * @param params Object
 * @param method String
 */
export const storePost = (module, data = {}, params = {}, method = 'post') => {
  return store.dispatch(`${module}/${method}`, { data, params })
}

/**
 * Dispatches the given module's "put" action.
 *
 * @param module String
 * @param data   Object
 * @param params Object
 * @param method String
 */
export const storePut = (module, data = {}, params = {}, method = 'put') => {
  return store.dispatch(`${module}/${method}`, { data, params })
}

/**
 * Dispatches the given module's "delete" action.
 *
 * @param module   String
 * @param resource String
 * @param method   String
 */
export const storeDelete = (module, resource, method = 'delete') => {
  return store.dispatch(`${module}/${method}`, resource)
}

/**
 * Performs a custom action on the specified module
 *
 * @param module String
 * @param data   Object
 * @param params Object
 * @param method String
 */
export const storeAction = (module, data = {}, params = {}, method) => {
  return store.dispatch(`${module}/${method}`, { data, params })
}

/**
 * Calls the search module's "pagination" getter.
 */
export const getPagination = (categoryName = 'default') => {
  let pagination = store.getters[SEARCH_CATEGORIES].find((el) => {
    return el.name === categoryName
  })

  if (!pagination) {
    return {
      current_page: null,
      first_item: null,
      last_item: null,
      next_page_url: null,
      prev_page_url: null,
      per_page: null
    }
  } else {
    return pagination.pagination
  }
}

/**
 * Calls the search module's "searchResults" getter.
 */
export const getSearchResults = (categoryName = 'default') => {
  let category = store.getters[SEARCH_CATEGORIES].find((el) => {
    return el.name === categoryName
  })

  if (!category) {
    setSearchCategory(categoryName)
    category = store.getters[SEARCH_CATEGORIES].find((el) => {
      return el.name === categoryName
    })
  }

  return category.results
}

/**
 * Calls the search module's "categories" getter.
 */
export const getSearchCategories = (categoryName = 'default') => {
  let category = store.getters[SEARCH_CATEGORIES].find((el) => {
    return el.name === categoryName
  })

  if (category) {
    return category
  }

  return store.getters[SEARCH_CATEGORIES]['default']
}

/**
 * Calls the search module's "setSearchResults" mutation.
 */
export const setSearchResults = (data, categoryName = 'default') => {
  store.commit(SEARCH_SET_SEARCH_RESULTS, {
    data,
    categoryName
  })
}

/**
 * Calls the search module's "setPagination" mutation.
 */
export const setSearchPagination = (data, categoryName = 'default') => {
  store.commit(SEARCH_SET_PAGINATION, {
    data,
    categoryName
  })
}

/**
 * Calls the search module's "setSearchQuery" mutation.
 */
export const setSearchQuery = (data, categoryName = 'default') => {
  store.commit(SEARCH_SET_SEARCH_QUERY, {
    data,
    categoryName
  })
}

/**
 * Calls the search module's "setSearchQuery" mutation.
 */
export const setSearchCategory = (categoryName) => {
  if (categoryName) {
    store.commit(SEARCH_SET_CATEGORIES, categoryName)
  }
}

export const purgeSearchResults = (categoryName = 'default') => {
  return store.commit(SEARCH_PURGE_SEARCH_RESULTS, categoryName)
}

/**
 * Adds data to recent history for the given module.
 *
 * @param module   String
 *
 * @return Array
 */
export const recentHistory = (module) => {
  let history = store.getters[RECENT_HISTORY]
  if (!history.hasOwnProperty(module)) {
    return []
  } else {
    return history[module]
  }
}

/**
 * Adds data to recent history for the given module.
 *
 * @param module   String
 * @param data Object
 */
export const addRecentHistory = (module, data) => {
  store.commit(RECENT_ADD_HISTORY, { module, data })
}

/**
 * Removes recent history for the given module.
 *
 * @param module   String
 */
export const removeRecentHistory = (module) => {
  store.commit(RECENT_REMOVE_HISTORY, module)
}

/**
 * Purge all recent history for all modules.
 */
export const purgeRecentHistory = () => {
  store.commit(RECENT_PURGE_HISTORY)
}

/**
 * Returns 'yes' for true, 'no' for false and '-1'
 * otherwise based on the given value in the given module.
 *
 * @param module String
 * @param field  String
 *
 * @returns String
 */
export const storeGetYesNo = (module, field) => {
  switch (storeGet(module)[field]) {
    case true:
      return 'yes'

    case false:
      return 'no'

    default:
      return '-1'
  }
}

/**
 * Sets the field in the specified module's value
 * to true if the value is 'yes', false if 'no' and null
 * otherwise.
 *
 * @param module String
 * @param field  String
 * @param value  String
 */
export const storeSetYesNo = (module, field, value) => {
  switch (value) {
    case 'yes':
      storeSetProp(module, { name: field, value: true })
      break

    case 'no':
      storeSetProp(module, { name: field, value: false })
      break

    default:
      storeSetProp(module, { name: field, value: null })
  }
}



// WEBPACK FOOTER //
// ./src/common/utils.js